/**
 * @module app.cd
 */

import { autoinject, transient } from 'aurelia-framework';
import { Api } from 'app/shared/lib/api/api';
import { IApiQueryResults, IQueryLoadOptions, IApiSettingsResults, IApiActionResults } from 'app/shared/lib/api/api-types';
import { IApiFilterResults } from 'app/core/models/api-results';
import { createQueryResults } from 'app/shared/lib/api/api-utilities';
import { AppLogger } from 'app/shared/lib/logging/app-logger';
import { Toaster } from 'app/shared/lib/toaster';
import { IBaseConfig } from 'app/core/models/essette-interfaces';
import * as CD from 'app/cd/models/essette-api';
import * as PM from 'app/pm/models/essette-api';
import * as PP from 'app/pp/models/essette-api';
import * as Core from 'app/core/models/essette-api';
import { EnvironmentService } from 'app/shared/services/environment-service';

export interface IMemberSearchFilters {
    generalSearchTerm?: string | null;
    generalIdentifier?: string | null;  /** Search across all identifiers */
    id?: string | null;
    memberId?: string | null;
    medicaidId?: string | null;
    ssn?: string | null;
    memberFirstName?: string | null;
    memberLastName?: string | null;
    memberPlan?: CD.MemberPlan | null;
    dob?: string | null;
    dobBetweenStart?: string | null;
    dobBetweenEnd?: string | null;
    eligibility?: boolean | null;
    healthPlan?: CD.HealthPlan | null;
    lineOfBusiness?: CD.LineOfBusiness | null;
    pcpId?: string | null;
    pcpFirstName?: string | null;
    pcpLastName?: string | null;
    householdName?: string | null;
    householdId?: string | null;
    exactMatch?: boolean | null;
    currentHistory?: boolean | null;
    alternateTypes?: boolean | null;
    numberIsFax?: boolean | null;
    gender?: Core.Gender | null;
    city?: string | null;
}

export function countMemberFilters(searchFilters: IMemberSearchFilters | null = null): number {
    if(!searchFilters) { return 0; }

    // TODO: we only ever make decisions based on count===0 or count>0, so this could be hasMemberFilters(): boolean
    // then a helper function like hasPropertyWhere(searchFilters, truthy, 'generalSearchTerm', ...) could do the
    // bulk of the work
    let count = 0;
    if(searchFilters.generalSearchTerm) { count += 1; }
    if(searchFilters.generalIdentifier) { count += 1; }
    if(searchFilters.id) { count += 1; }
    if(searchFilters.memberId) { count += 1; }
    if(searchFilters.medicaidId) { count += 1; }
    if(searchFilters.ssn) { count += 1; }
    if(searchFilters.memberFirstName) { count += 1; }
    if(searchFilters.memberLastName) { count += 1; }
    if(searchFilters.memberPlan) { count += 1; }
    if(searchFilters.dob) { count += 1; }
    if(searchFilters.dobBetweenStart) { count += 1; }
    if(searchFilters.dobBetweenEnd) { count += 1; }
    if(!!searchFilters.eligibility) { count += 1; }
    if(searchFilters.healthPlan?.id) { count += 1; }
    if(searchFilters.lineOfBusiness?.id) { count += 1; }
    if(searchFilters.pcpId) { count += 1; }
    if(searchFilters.pcpFirstName) { count += 1; }
    if(searchFilters.pcpLastName) { count += 1; }
    if(searchFilters.householdName) { count += 1; }
    if(searchFilters.householdId) { count += 1; }
    if(searchFilters.gender) { count += 1; }
    if(searchFilters.city) { count += 1; }

    return count;
}

export enum PhoneNumberSystemCode {
    All = "",
    Home = "CD.Member.Home",
    Cell = "CD.Member.Cell",
    Work = "CD.Member.Work",
    Fax = "CD.Member.Fax"
}

export enum AddressSystemCode {
    All = "",
    Home = "CD.Member.Home",
    Mailing = "CD.Member.Mailing",
    Work = "CD.Member.Work"
}

export enum MemberType {
    Patient = "Patient",
    Member = "Member"
}

const defaultConfig: IBaseConfig = {
    serviceRootUrl: 'cd/members/plans',
    moduleCode: 'cd'
}

@transient()
@autoinject()
export class MemberService {

    private isConfigured: boolean = false;
    private config: IBaseConfig;

    constructor(
        private readonly api: Api,
        private readonly logger: AppLogger,
        private readonly toaster: Toaster,
        private readonly env: EnvironmentService) {

        this.logger.source = this;
        this.config = defaultConfig;
    }

    public configure(config: IBaseConfig): void {
        this.config = Object.assign({}, defaultConfig, config); // Clones the incoming config object parameter, using defaultConfig as fallback for any missing properties, and assigns it to this.config
        if(this.env.application.code === 'Portal.Providers') {
            this.config.moduleCode = 'pp';
        }
        this.isConfigured = true;
    }

    private checkConfiguration(): void {
        if(!this.isConfigured) {
            this.configure(defaultConfig);
        }
    }

    ////****************** Settings *************************************************////
    public async loadMemberPlanSettings(includes: string[] = []): Promise<IApiSettingsResults> {
        this.checkConfiguration();

        return this.api.get(
            this.api.settings(`${this.config.serviceRootUrl}`).include(...includes)
        );
    }

    ////****************** Queries **************************************************////
    public async search(
        searchFilters: IMemberSearchFilters,
        options?: Partial<IQueryLoadOptions>,
        includes: string[] = [],
        fields: string[] = []): Promise<IApiQueryResults<CD.Member>> {

        this.checkConfiguration();

        const count = countMemberFilters(searchFilters);
        if(count === 0) {
            this.logger.warn('Invalid or missing search criteria');
            this.toaster.standard.searchNoCriteria(`${this.config.moduleCode}:member.$`);
            return createQueryResults('CD.Member', [], true);
        }

        const query = this.api
            .query('cd/members')
            .strategy('search')
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);


        if(count > 0) {
            if(searchFilters.generalSearchTerm) {
                query.search(searchFilters.generalSearchTerm);
            }
            else {
                if(searchFilters.id) {
                    query.filterIdentifier(
                        'member.id',
                        searchFilters.id);
                }
                if(searchFilters.memberId) {
                    query.filterString(
                        'identifier.primary',
                        searchFilters.memberId,
                        'startsWith');
                }
                if(searchFilters.medicaidId) {
                    query.filterString(
                        'identifier.medicaid',
                        searchFilters.medicaidId,
                        'startsWith'
                    );
                }
                if(searchFilters.ssn) {
                    let hyphenSsn = searchFilters.ssn;
                    const integerSsn = hyphenSsn.replace(/-/g, '');
                    // if user input did not include hyphens, build a properly hyphenated variant
                    if(hyphenSsn === integerSsn) {
                        if(integerSsn.length > 3 && integerSsn.length <= 5) hyphenSsn = `${integerSsn.slice(0, 3)}-${integerSsn.slice(3)}`;
                        else if(integerSsn.length > 5) hyphenSsn = `${integerSsn.slice(0, 3)}-${integerSsn.slice(3, 5)}-${integerSsn.slice(5)}`;
                    }
                    // if the variants are the same, search by one
                    if(hyphenSsn === integerSsn) {
                        query.filterString('identifier.socialSecurityNumber', integerSsn, 'startsWith')
                    } else {
                        // otherwise, search by both
                        query.filterOr(query => query
                            .filterString('identifier.socialSecurityNumber', integerSsn, 'startsWith')
                            .filterString('identifier.socialSecurityNumber', hyphenSsn, 'startsWith')
                        );
                    }
                }
                if(searchFilters.memberFirstName) {
                    query.filterString(
                        'name.first',
                        searchFilters.memberFirstName,
                        'startsWith');
                }
                if(searchFilters.memberLastName) {
                    query.filterString(
                        'name.last',
                        searchFilters.memberLastName,
                        'startsWith');
                }
                if(searchFilters.dob) {
                    query.filterDate('birthDate', searchFilters.dob);
                }
                if(searchFilters.dobBetweenStart && searchFilters.dobBetweenEnd) {
                    query.filterDateBetween('birthDate', searchFilters.dobBetweenStart, searchFilters.dobBetweenEnd);
                }
                if(!!searchFilters.eligibility) {
                    query.filterBoolean('eligibility.isCurrent', true);
                }
                if(searchFilters.healthPlan && searchFilters.healthPlan.id) {
                    query.filterIdentifier(
                        'primaryPlan.healthPlan.id',
                        searchFilters.healthPlan.id);
                }
                if(searchFilters.lineOfBusiness && searchFilters.lineOfBusiness.id) {
                    query.filterIdentifier(
                        'primaryPlan.lineOfBusiness.id',
                        searchFilters.lineOfBusiness.id);
                }
                if(searchFilters.pcpId) {
                    query.filterString(
                        'primaryCarePhysician.identifier.primary',
                        searchFilters.pcpId,
                        'startsWith');
                }
                if(searchFilters.pcpFirstName) {
                    query.filterString(
                        'primaryCarePhysician.name.first',
                        searchFilters.pcpFirstName,
                        'startsWith');
                }
                if(searchFilters.pcpLastName) {
                    query.filterString(
                        'primaryCarePhysician.name.last',
                        searchFilters.pcpLastName,
                        'startsWith');
                }
                if(searchFilters.householdName) {
                    query.filterExists('household.memberships', (f =>
                        f.filterString('household.description', searchFilters.householdName!, 'startsWith')
                    ));
                }
                if(searchFilters.householdId) {
                    query.filterExists('household.memberships', (f =>
                        f.filterString('household.identifier.primary', searchFilters.householdId!, 'startsWith')
                    ));
                }
                if(searchFilters.gender?.id) {
                    query.filterIdentifier('gender.id', searchFilters.gender.id);
                }
                if(searchFilters.city) {
                    query.filterString(
                        'address.home.city',
                        searchFilters.city,
                        'startsWith');
                }
            }
        }

        return await this.api.get(query);
    }

    public async loadLabResultsByMember(memberID: string, options?: Partial<IQueryLoadOptions>, includes: string[] = [], fields: string[] = []): Promise<IApiQueryResults<CD.LabResult>> {
        const query = this.api
            .query('cd/labs/results/')
            .filterIdentifier('member.id', memberID)
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);

        return await this.api.get(query);
    }

    public async loadClaimsByMember(memberId: string, options?: Partial<IQueryLoadOptions>, includes: string[] = [], fields: string[] = []): Promise<IApiQueryResults<CD.Claim>> {
        const query = this.api
            .query('cd/claims/')
            .filterIdentifier('member.id', memberId)
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);
        return await this.api.get(query);
    }

    public async generalSearch(searchTerm: string, options?: Partial<IQueryLoadOptions>, includes: string[] = []): Promise<IApiQueryResults<CD.Member> | null> {
        const query = this.api
            .query(`cd/members`)
            .strategy('quick')
            .search(searchTerm)
            .applyStandardLoadOptions(options)
            .include(...includes);
        return await this.api.get(query);

    }

    public async searchMemberPlans(searchFilters: IMemberSearchFilters, options?: Partial<IQueryLoadOptions>, includes: string[] = [], fields: string[] = []): Promise<IApiQueryResults<CD.MemberPlan>> {
        this.checkConfiguration();

        const count = countMemberFilters(searchFilters);
        if(count === 0) {
            this.logger.warn('Invalid or missing search criteria');
            this.toaster.standard.searchNoCriteria('cd:member.$');
            return createQueryResults('CD.MemberPlan', [], true);
        }

        const query = this.api
            .query(`${this.config.serviceRootUrl}`)
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);

        if(count > 0) {
            if(searchFilters.generalSearchTerm) {
                query
                    .strategy('quick')
                    .search(searchFilters.generalSearchTerm);
            }
            else if(count === 1 && searchFilters.generalIdentifier) {
                query.filterOr(f => {
                    const operator = (searchFilters.exactMatch) ? 'eq' : 'startsWith';
                    f.filterString('identifier.primary', searchFilters.generalIdentifier!, operator);
                    f.filterString('member.identifier.medicaid', searchFilters.generalIdentifier!, operator);
                });
            }
            else {
                query.strategy('search');
                if(searchFilters.id) {
                    query.filterIdentifier(
                        `member.id`,
                        searchFilters.id);
                }
                if(searchFilters.memberId) {
                    query.filterString(
                        'identifier.primary',
                        searchFilters.memberId,
                        searchFilters.exactMatch ? 'eq' : 'startsWith');
                }
                if(searchFilters.medicaidId) {
                    query.filterString(
                        'member.identifier.medicaid',
                        searchFilters.medicaidId,
                        searchFilters.exactMatch ? 'eq' : 'startsWith');
                }
                if(searchFilters.memberFirstName) {
                    query.filterString(
                        `member.name.first`,
                        searchFilters.memberFirstName,
                        searchFilters.exactMatch ? 'eq' : 'startsWith');
                }
                if(searchFilters.memberLastName) {
                    query.filterString(
                        `member.name.last`,
                        searchFilters.memberLastName,
                        searchFilters.exactMatch ? 'eq' : 'startsWith');
                }
                if(searchFilters.dob) {
                    query.filterDate(`member.birthDate`, searchFilters.dob);
                }
                if(!!searchFilters.eligibility) {
                    query.filterBoolean(`member.eligibility.isCurrent`, true);
                }
                if(searchFilters.healthPlan && searchFilters.healthPlan.id) {
                    query.filterIdentifier(
                        `member.primaryPlan.healthPlan.id`,
                        searchFilters.healthPlan.id);
                }
                if(searchFilters.lineOfBusiness && searchFilters.lineOfBusiness.id) {
                    query.filterIdentifier(
                        `member.primaryPlan.lineOfBusiness.id`,
                        searchFilters.lineOfBusiness.id);
                }
                if(searchFilters.pcpId) {
                    query.filterString(
                        `member.primaryCarePhysician.identifier.primary`,
                        searchFilters.pcpId,
                        searchFilters.exactMatch ? 'eq' : 'startsWith');
                }
                if(searchFilters.pcpFirstName) {
                    query.filterString(
                        `member.primaryCarePhysician.name.first`,
                        searchFilters.pcpFirstName,
                        searchFilters.exactMatch ? 'eq' : 'startsWith');
                }
                if(searchFilters.pcpLastName) {
                    query.filterString(
                        `member.primaryCarePhysician.name.last`,
                        searchFilters.pcpLastName,
                        searchFilters.exactMatch ? 'eq' : 'startsWith');
                }
            }
        }

        return await this.api.get(query);
    }

    ////****************** Loads ****************************************************////
    public async loadMember(memberId: string, includes: string[] = [], fields: string[] = []): Promise<IApiQueryResults<CD.Member>> {
        const query = this.api
            .query(`cd/members/${memberId}`)
            .include(...includes)
            .field(...fields);

        return await this.api.get(query);
    }

    public async loadMembersByIds(memberIds: string[], options?: Partial<IQueryLoadOptions>, includes: string[] = [], fields: string[] = []): Promise<IApiQueryResults<CD.Member>> {
        const query = this.api
            .query(`cd/members/`)
            .filterIdentifierArray('id', memberIds, 'eq')
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);

        return await this.api.get(query);
    }

    public async loadPatient(memberId: string, includes: string[] = [], fields: string[] = []): Promise<IApiQueryResults<PP.AvailableMember>> {
        const query = this.api
            .query(`pp/members/available/${memberId}`)
            .include(...includes)
            .field(...fields);

        return await this.api.get(query);
    }

    public async loadMemberByPlan(memberPlanId: string, includes: string[] = [], fields: string[] = []): Promise<IApiQueryResults<CD.Member>> {
        const query = this.api
            .query(`cd/members`)
            .filterExists('plans', q => q.filterIdentifier('id', memberPlanId))
            .include(...includes)
            .field(...fields);
        return await this.api.get(query);
    }

    public async loadCandidatesByMember(memberId: string, options?: Partial<IQueryLoadOptions>, includes: string[] = [], fields: string[] = []): Promise<IApiQueryResults<PM.Campaign>> {
        const query = this.api
            .query('pm/candidates/')
            .filterIdentifier('member.id', memberId)
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);

        return await this.api.get(query);
    }

    public async loadMemberPlan(memberPlanId: string, includes: string[] = [], fields: string[] = []): Promise<IApiQueryResults<CD.MemberPlan>> {
        const query = this.api
            .query(`${this.config.serviceRootUrl}/${memberPlanId}`)
            .include(...includes)
            .field(...fields);

        return await this.api.get(query);
    }

    public async loadPlansByMember(memberId: string, options?: Partial<IQueryLoadOptions>, includes: string[] = [], fields: string[] = []): Promise<IApiQueryResults<CD.MemberPlan>> {
        const query = this.api
            .query(`cd/members/${memberId}/plans/`)
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);

        return await this.api.get(query);
    }

    public async searchMemberPlansWithId(memberId: string, searchValue: string, options?: Partial<IQueryLoadOptions>, includes: string[] = [], fields: string[] = []): Promise<IApiQueryResults<CD.MemberPlan>> {
        const query = this.api
            .query(`cd/members/${memberId}/plans/`)
            .filterOr(query => {
                query.filterString('healthPlan.description', searchValue, 'contains');
                query.filterString('lineOfBusiness.description', searchValue, 'contains');
            })
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);

        return await this.api.get(query);
    }

    public async loadPlansByPatient(memberId: string, options?: Partial<IQueryLoadOptions>, includes: string[] = [], fields: string[] = []): Promise<IApiQueryResults<CD.MemberPlan>> {
        const query = this.api
            .query(`pp/members/available/${memberId}/plans/`)
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);

        return await this.api.get(query);
    }

    public async loadMemberPharmacyClaims(memberId: string, options?: Partial<IQueryLoadOptions>, includes: string[] = [], fields: string[] = []): Promise<IApiQueryResults<CD.PharmacyClaim>> {
        const query = this.api
            .query(`cd/members/${memberId}/pharmacyClaims/`)
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);

        return await this.api.get(query);
    }

    public async loadMemberImportedAuths(memberId: string, options?: Partial<IQueryLoadOptions>, includes: string[] = [], fields: string[] = []): Promise<IApiQueryResults<CD.Auth>> {
        const query = this.api
            .query(`cd/auths/`)
            .filterIdentifier('member.id', memberId)
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);

        return await this.api.get(query);
    }

    public async loadMemberContacts(memberId: string, options?: Partial<IQueryLoadOptions>, includes: string[] = [], fields: string[] = []): Promise<IApiQueryResults<CD.MemberContact>> {
        const query = this.api
            .query(`cd/members/${memberId}/contacts/`)
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);

        return await this.api.get(query);
    }

    public async loadPatientContacts(memberId: string, options?: Partial<IQueryLoadOptions>, includes: string[] = [], fields: string[] = []): Promise<IApiQueryResults<CD.MemberContact>> {
        const query = this.api
            .query(`pp/members/available/${memberId}/contacts/`)
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);

        return await this.api.get(query);
    }

    public async loadMemberContactsComposite(memberId: string, options?: Partial<IQueryLoadOptions>, includes: string[] = [], fields: string[] = []): Promise<IApiQueryResults<CD.MemberContactComposite>> {
        const query = this.api
            .query(`cd/members/contacts/composite`)
            .filterIdentifier('member.id', memberId)
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);

        return await this.api.get(query);
    }

    public async loadMemberEligibilities(memberId: string, options?: Partial<IQueryLoadOptions>, includes: string[] = [], fields: string[] = []): Promise<IApiQueryResults<CD.MemberEligibility>> {
        const query = this.api
            .query(`cd/members/${memberId}/history/eligibility/`)
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);

        return await this.api.get(query);
    }

    public async loadMemberEligibilitiesByPlanId(memberId: string, planId: string, options?: Partial<IQueryLoadOptions>, includes: string[] = [], fields: string[] = []): Promise<IApiQueryResults<CD.MemberEligibility>> {
        const query = this.api
            .query(`cd/members/${memberId}/history/eligibility/`)
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields)
            .filterIdentifier('member.plan.id', planId);

        return await this.api.get(query);
    }

    public async loadPatientEligibilities(memberId: string, options?: Partial<IQueryLoadOptions>, includes: string[] = [], fields: string[] = []): Promise<IApiQueryResults<CD.MemberEligibility>> {
        const query = this.api
            .query(`pp/members/available/${memberId}/history/eligibility/`)
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);

        return await this.api.get(query);
    }

    public async loadMemberEligibilityById(eligibilityId: string, options?: Partial<IQueryLoadOptions>, includes: string[] = []): Promise<IApiQueryResults<CD.MemberEligibility>> {
        const query = this.api
            .query(`cd/members/eligibility/${eligibilityId}`)
            .applyStandardLoadOptions(options)
            .include(...includes)

        return await this.api.get(query);
    }

    public async loadMemberAidCodesByPlan(planId: string, includes: string[] = [], searchFilters?: IMemberSearchFilters, options?: Partial<IQueryLoadOptions>, fields: string[] = []): Promise<IApiQueryResults<CD.MemberAidCode>> {
        const query = this.api
            .query(`cd/members/aidcodes/`)
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields)
            .filterIdentifier('member.plan.id', planId);

        if(searchFilters?.currentHistory) {
            query.filterOr((or) => {
                or.filterDate('dateRange.end', null);
                or.filterDateSpecial('dateRange.end', 'today', 'gte')
            });
        }

        return await this.api.get(query);
    }

    public async loadMemberConditionsByPlan(planId: string, includes: string[] = [], searchFilters?: IMemberSearchFilters, options?: Partial<IQueryLoadOptions>, fields: string[] = []): Promise<IApiQueryResults<CD.MemberCondition>> {
        const query = this.api
            .query(`cd/members/conditions/`)
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields)
            .filterIdentifier('member.plan.id', planId);

        if(searchFilters?.currentHistory) {
            query.filterOr((or) => {
                or.filterDate('dateRange.end', null);
                or.filterDateSpecial('dateRange.end', 'today', 'gte')
            });
        }

        return await this.api.get(query);
    }

    public async loadPatientConditionsByPlan(planId: string, includes: string[] = [], searchFilters?: IMemberSearchFilters, options?: Partial<IQueryLoadOptions>, fields: string[] = []): Promise<IApiQueryResults<CD.MemberCondition>> {
        const query = this.api
            .query(`pp/members/plans/available/${planId}/history/conditions/`)
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);

        if(searchFilters?.currentHistory) {
            query.filterOr((or) => {
                or.filterDate('dateRange.end', null);
                or.filterDateSpecial('dateRange.end', 'today', 'gte')
            });
        }

        return await this.api.get(query);
    }

    public async loadMemberContactDetails(memberId: string): Promise<IApiQueryResults<any>> { // TODO: Change any to CD.MemberContactPreference when API is updated
        const query = this.api
            .query(`cd/members/${memberId}/contacts/preferences`)

        return await this.api.get(query);
    }

    public async loadMemberAddressTypes(options?: Partial<IQueryLoadOptions>, includes: string[] = [], searchFilters?: IMemberSearchFilters): Promise<IApiQueryResults<CD.MemberAddressType>> {
        const query = this.api
            .query('cd/members/addresses/types')
            .applyStandardLoadOptions(options)
            .include(...includes);

        //If alternateTypes is declared and true, only get alternate types (where system code is null). If its false, only get primary types (where system code is not null)
        if(searchFilters?.alternateTypes !== undefined) {
            query.filterString('code', null, searchFilters?.alternateTypes ? 'eq' : 'ne');
        }

        return await this.api.get(query);
    }

    public async loadMemberProviderRelationships(options?: Partial<IQueryLoadOptions>, includes: string[] = [], fields: string[] = []): Promise<IApiQueryResults<CD.MemberProviderRelationship>> {
        const query = this.api
            .query('cd/members/providers/relationships')
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);

        return await this.api.get(query);
    }

    public async loadMemberContactAddressTypes(options?: Partial<IQueryLoadOptions>, includes: string[] = []): Promise<IApiQueryResults<CD.MemberAddressType>> {
        const query = this.api
            .query('cd/members/contacts/addresses/types')
            .applyStandardLoadOptions(options)
            .include(...includes);

        return await this.api.get(query);
    }

    public async loadInactiveContactPhoneNumbersByContact(contactId: string, options?: Partial<IQueryLoadOptions>, includes: string[] = [], fields: string[] = []): Promise<IApiQueryResults<CD.MemberContactPhoneNumber>> {
        const query = this.api
            .query('cd/members/contacts/phoneNumbers')
            .applyStandardLoadOptions(options)
            .filterString('Contact_ID', contactId, 'eq')
            .filterBoolean('isActive', false)
            .include(...includes)
            .field(...fields);

        return await this.api.get(query);
    }

    public async loadMemberPhoneNumberTypes(options?: Partial<IQueryLoadOptions>, includes: string[] = [], searchFilters?: IMemberSearchFilters): Promise<IApiQueryResults<CD.MemberPhoneNumberType>> {
        const query = this.api
            .query('cd/members/phoneNumbers/types')
            .applyStandardLoadOptions(options)
            .include(...includes);

        if(searchFilters?.alternateTypes) {
            query.filterString('systemCode', null);
        }

        return await this.api.get(query);
    }

    public async loadMemberContactPhoneNumberTypes(options?: Partial<IQueryLoadOptions>, includes: string[] = []): Promise<IApiQueryResults<CD.MemberPhoneNumberType>> {
        const query = this.api
            .query('cd/members/contacts/phoneNumbers/types')
            .applyStandardLoadOptions(options)
            .include(...includes);

        return await this.api.get(query);
    }

    public async loadMemberSummaryNotes(memberId: string, options?: Partial<IQueryLoadOptions>, includes: string[] = [], fields: string[] = []): Promise<IApiQueryResults<CD.MemberRollupNote>> {
        const query = this.api
            .query(`cd/members/${memberId}/rollup/notes`)
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields)
            .filterExists('flags', q => q.filterString('code', 'CD.MemberHistory'));


        return await this.api.get(query);
    }

    public async loadMemberRelationships(options?: Partial<IQueryLoadOptions>, includes: string[] = []): Promise<IApiQueryResults<CD.MemberRelationship>> {
        const query = this.api
            .query('cd/members/relationships')
            .applyStandardLoadOptions(options)
            .include(...includes);

        return await this.api.get(query);
    }

    public async loadMemberContactTypes(options?: Partial<IQueryLoadOptions>, includes: string[] = []): Promise<IApiQueryResults<CD.MemberContactType>> {
        const query = this.api
            .query('cd/members/contacts/types')
            .applyStandardLoadOptions(options)
            .include(...includes);

        return await this.api.get(query);
    }

    public async loadRaces(options?: Partial<IQueryLoadOptions>, includes: string[] = []): Promise<IApiQueryResults<CD.Race>> {
        const query = this.api
            .query('cd/races')
            .applyStandardLoadOptions(options)
            .include(...includes);

        return await this.api.get(query);
    }

    public async loadMemberAddresses(memberId: string, systemCode?: AddressSystemCode, options?: Partial<IQueryLoadOptions>, includes: string[] = [], fields: string[] = [], searchFilters?: IMemberSearchFilters): Promise<IApiQueryResults<CD.MemberAddress>> {
        const query = this.api
            .query(`cd/members/${memberId}/addresses`)
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);

        if(systemCode) {
            query.filterString('systemCode', systemCode);
        }

        if(searchFilters?.alternateTypes !== null && searchFilters?.alternateTypes !== undefined) {
            if(searchFilters?.alternateTypes) {
                query.filterString('type.systemCode', null);
            } else {
                query.filterNot(not => not.filterString('type.systemCode', null));
            }
        }

        return await this.api.get(query);
    }

    public async loadMemberPhoneNumbers(memberId: string, systemCode?: PhoneNumberSystemCode, options?: Partial<IQueryLoadOptions>, includes: string[] = [], fields: string[] = [], searchFilters?: IMemberSearchFilters): Promise<IApiQueryResults<CD.MemberPhoneNumber>> {
        const query = this.api
            .query(`cd/members/${memberId}/phonenumbers`)
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);

        if(systemCode) {
            query.filterString('systemCode', systemCode);
        }

        if(searchFilters?.alternateTypes) {
            query.filterString('type.systemCode', null);
        }

        if(searchFilters?.numberIsFax) {
            query.filterBoolean('isFax', true);
        }

        return await this.api.get(query);
    }

    public async loadContactAddressByContact(contactId: string, addressTypeId: string, options?: Partial<IQueryLoadOptions>, includes: string[] = [], fields: string[] = []): Promise<IApiQueryResults<CD.ProviderGroupAddress>> {
        const query = this.api
            .query('cd/members/contacts/addresses')
            .applyStandardLoadOptions(options)
            .filterString('Contact_ID', contactId, 'eq')
            .filterString('type.id', addressTypeId, 'eq')
            .include(...includes)
            .field(...fields);

        return await this.api.get(query);
    }

    ////****************** Actions **************************************************////
    public async saveMember(member: CD.Member): Promise<IApiActionResults> {
        const actionName = !member.id ? 'insert' : 'update';
        const action = this.api.action(actionName, `cd/members/${actionName === 'update' ? member.id : ''}`, member);

        return await this.api.post(action);
    }

    public async saveMemberPlan(memberPlan: CD.MemberPlan): Promise<IApiActionResults> {
        const actionName = !memberPlan.id ? 'insert' : 'update';
        const action = this.api.action(actionName, `cd/members/plans/${actionName === 'update' ? memberPlan.id : ''}`, memberPlan);

        return await this.api.post(action);
    }

    public async updateMemberEmail(memberId: string, emailAddress: string | null): Promise<IApiActionResults> {
        const action = this.api.action('updateContact', `cd/members/${memberId}`, {
            emailAddress
        });

        return await this.api.post(action);
    }

    public async saveMemberAddress(address: CD.MemberAddress): Promise<IApiActionResults> {
        const actionName = !address.id ? 'insert' : 'update';

        const action = this.api.action(actionName, `cd/members/addresses/${actionName === 'update' ? address.id : ''}`, address);

        return await this.api.post(action);
    }

    public async activateMemberAddress(addressId: string, isActive: boolean): Promise<IApiActionResults> {
        const actionCode = isActive ? 'reactivate' : 'deactivate';
        const action = this.api.action(actionCode, `cd/members/addresses/${addressId}`, { isActive: isActive });
        if(isActive) {
            return await this.api.post(action);
        } else {
            return await this.api.delete(action);
        }
    }

    public async saveMemberDemographics(memberDemographics: CD.MemberDemographics): Promise<IApiActionResults> {
        const actionName = 'save';

        const action = this.api.action(actionName, '/CD/Members/Demographics/', memberDemographics!);

        return await this.api.post(action);
    }

    public async saveMemberPhoneNumber(phoneNumber: CD.MemberPhoneNumber): Promise<IApiActionResults> {
        const actionName = !phoneNumber.id ? 'insert' : 'update';

        const action = this.api.action(actionName, `cd/members/phoneNumbers/${actionName === 'update' ? phoneNumber.id : ''}`, phoneNumber);

        return await this.api.post(action);
    }

    public async activateMemberPhoneNumber(phoneNumberId: string, isActive: boolean): Promise<IApiActionResults> {
        const actionCode = isActive ? 'reactivate' : 'deactivate';
        const action = this.api.action(actionCode, `cd/members/phoneNumbers/${phoneNumberId}`, { isActive: isActive });
        if(isActive) {
            return await this.api.post(action);
        } else {
            return await this.api.delete(action);
        }
    }

    public async activateMemberContactPhoneNumber(phoneNumberId: string, isActive: boolean): Promise<IApiActionResults> {
        const actionCode = isActive ? 'reactivate' : 'deactivate';
        const action = this.api.action(actionCode, `cd/members/contacts/phoneNumbers/${phoneNumberId}`, { isActive: isActive });
        if(isActive) {
            return await this.api.post(action);
        } else {
            return await this.api.delete(action);
        }
    }

    public async saveMemberContact(contact: CD.MemberContact): Promise<IApiActionResults> {
        const actionName = !contact.id ? 'insert' : 'update';
        const action = this.api.action(actionName, `cd/members/contacts/${actionName === 'update' ? contact.id : ''}`, contact);

        return await this.api.post(action);
    }

    public async handleMemberContactAddress(address: CD.MemberContactAddress): Promise<IApiActionResults> {
        const actionName = (!address.id && address.street1) ? 'insert' : address.street1 ? 'update' : 'deactivate';
        const action = this.api.action(actionName, `cd/members/contacts/addresses/${actionName !== 'insert' ? address.id : ''}`, address!);
        return actionName !== 'deactivate' ? await this.api.post(action) : await this.api.delete(action);
    }

    public async handleMemberContactPhoneNumber(phoneNumber: CD.MemberContactPhoneNumber): Promise<IApiActionResults> {
        const actionName = (!phoneNumber.id && phoneNumber.number) ? 'insert' : phoneNumber.number ? 'update' : 'deactivate';
        const action = this.api.action(actionName, `cd/members/contacts/phoneNumbers/${actionName !== 'insert' ? phoneNumber.id : ''}`, phoneNumber!);
        return actionName !== 'deactivate' ? await this.api.post(action) : await this.api.delete(action);
    }

    public async saveMemberContactDetails(contactPreference: CD.MemberContactPreference): Promise<IApiActionResults> {
        const action = this.api.action('save', 'cd/members/contacts/preferences/', contactPreference);

        return await this.api.post(action);
    }

    public async activateMemberContact(contactId: string, isActive: boolean): Promise<IApiActionResults> {
        const actionCode = isActive ? 'reactivate' : 'deactivate';
        const action = this.api.action(actionCode, `cd/members/contacts/${contactId}`, { isActive: isActive });
        if(isActive) {
            return await this.api.post(action);
        } else {
            return await this.api.delete(action);
        }
    }

    public async activateMemberContactAddress(addressId: string, isActive: boolean): Promise<IApiActionResults> {
        const actionCode = isActive ? 'reactivate' : 'deactivate';
        const action = this.api.action(actionCode, `cd/members/contacts/addresses/${addressId}`, { isActive: isActive });
        if(isActive) {
            return await this.api.post(action);
        } else {
            return await this.api.delete(action);
        }
    }

    public async activateMemberProvider(memberProviderId: string, isActive: boolean): Promise<IApiActionResults> {
        const actionCode = isActive ? 'reactivate' : 'deactivate';
        const action = this.api.action(actionCode, `cd/members/providers/${memberProviderId}`, { isActive: isActive });

        if(isActive) {
            return await this.api.post(action);
        } else {
            return await this.api.delete(action);
        }
    }

    public async saveMemberProviderRelationship(relationship: CD.MemberProviderRelationship): Promise<IApiActionResults> {
        const actionName = !relationship.id ? 'insert' : 'update';
        const action = this.api.action(actionName, `cd/members/providers/relationships/${actionName === 'update' ? relationship.id : ''}`, relationship);

        return await this.api.post(action);
    }

    public async activateMemberProviderRelationship(relationshipId: string, isActive: boolean): Promise<IApiActionResults> {
        const actionCode = isActive ? 'reactivate' : 'deactivate';
        const action = this.api.action(actionCode, `cd/members/providers/relationships/${relationshipId}`, { isActive: isActive });

        if(isActive) {
            return await this.api.post(action);
        } else {
            return await this.api.delete(action);
        }
    }

    public async saveMemberProvider(memberProvider: CD.MemberProvider): Promise<IApiActionResults> {
        const actionName = !memberProvider.id ? 'insert' : 'update';
        const action = this.api.action(actionName, `cd/members/providers/${actionName === 'update' ? memberProvider.id : ''}`, memberProvider);

        return await this.api.post(action);
    }


    public async saveEthnicity(ethnicity: CD.Ethnicity): Promise<IApiActionResults> {
        const actionName = !ethnicity.id ? 'insert' : 'update';
        const action = this.api.action(actionName, `cd/ethnicities/${ethnicity.id ? `${ethnicity.id}` : ``}`, ethnicity);

        return await this.api.post(action);
    }

    public async activateEthnicity(id: string, isActive: boolean): Promise<IApiActionResults> {
        const actionCode = isActive ? 'reactivate' : 'deactivate';
        const action = this.api.action(actionCode, `cd/ethnicities/${id}/`, { isActive: isActive });
        if(isActive) {
            return await this.api.post(action);
        } else {
            return await this.api.delete(action);
        }
    }

    public async saveRace(race: CD.Race): Promise<IApiActionResults> {
        const actionName = !race.id ? 'insert' : 'update';
        const action = this.api.action(actionName, `/cd/races/${race.id ? `${race.id}` : ``}`, race);

        return await this.api.post(action);
    }

    public async activateRace(raceId: string, isActive: boolean): Promise<IApiActionResults> {
        const actionCode = isActive ? 'reactivate' : 'deactivate';
        const url = `cd/races/${raceId}`;
        const action = this.api.action(actionCode, url, { isActive: isActive })

        if(isActive) {
            return await this.api.post(action);
        } else {
            return await this.api.delete(action);
        }
    }

    public async saveGender(gender: Core.Gender): Promise<IApiActionResults> {
        const actionName = !gender.id ? 'insert' : 'update';
        const action = this.api.action(actionName, `/genders/${gender.id ? `${gender.id}` : ``}`, gender);

        return await this.api.post(action);
    }

    public async activateGender(genderId: string, isActive: boolean): Promise<IApiActionResults> {
        const actionCode = isActive ? 'reactivate' : 'deactivate';
        const url = `genders/${genderId}`;
        const action = this.api.action(actionCode, url, { isActive: isActive })

        if(isActive) {
            return await this.api.post(action);
        } else {
            return await this.api.delete(action);
        }
    }

    public async saveMemberCondition(memberCondition: CD.MemberCondition): Promise<IApiActionResults> {
        const actionName = !memberCondition.id ? 'insert' : 'update';
        const action = this.api.action(actionName, `cd/members/conditions/${actionName === 'update' ? memberCondition.id : ''}`, memberCondition);

        return await this.api.post(action);
    }

    public async saveMemberAidCode(memberAidCode: CD.MemberAidCode): Promise<IApiActionResults> {
        const actionName = !memberAidCode.id ? 'insert' : 'update';
        const action = this.api.action(actionName, `cd/members/aidcodes/${actionName === 'update' ? memberAidCode.id : ''}`, memberAidCode);

        return await this.api.post(action);
    }

    public async activateMemberPlanEligibility(eligibilityId: string, isActive: boolean): Promise<IApiActionResults> {
        const actionCode = isActive ? 'reactivate' : 'deactivate';
        const action = this.api.action(actionCode, `cd/members/eligibility/${eligibilityId}`, { isActive: isActive });
        if(isActive) {
            return await this.api.post(action);
        } else {
            return await this.api.delete(action);
        }
    }

    public async saveEligibility(eligibilityProduct: CD.MemberEligibility): Promise<IApiActionResults> {
        const actionName = !eligibilityProduct.id ? 'insert' : 'update';
        const action = this.api.action(actionName, `cd/members/eligibility/${actionName === 'update' ? eligibilityProduct.id : ''}`, eligibilityProduct);

        return await this.api.post(action);
    }

    public async activateMemberCondition(memberConditionId: string, isActive: boolean): Promise<IApiActionResults> {
        const actionCode = isActive ? 'reactivate' : 'deactivate';
        const action = this.api.action(actionCode, `cd/members/conditions/${memberConditionId}`, { isActive: isActive });
        if(isActive) {
            return await this.api.post(action);
        } else {
            return await this.api.delete(action);
        }
    }

    public async activateMemberAidCode(memberAidCodeId: string, isActive: boolean): Promise<IApiActionResults> {
        const actionCode = isActive ? 'reactivate' : 'deactivate';
        const action = this.api.action(actionCode, `cd/members/aidcodes/${memberAidCodeId}`, { isActive: isActive });
        if(isActive) {
            return await this.api.post(action);
        } else {
            return await this.api.delete(action);
        }
    }

    public async loadMemberMergeResults(memberId: string, options?: Partial<IQueryLoadOptions>, includes: string[] = [], fields: string[] = []): Promise<IApiQueryResults<CD.MemberMergeResultDetail>> {
        const query = this.api
            .query(`cd/members/mergeresults/`)
            .filterOr((query) => {
                query.filterString('target.id', memberId, 'eq');
                query.filterString('source.id', memberId, 'eq');
            })
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);

        return await this.api.get(query);
    }

    public async verifyMemberAccess(userId: string, members: CD.Member[]): Promise<IApiFilterResults> {
        const body = { filter: { model: 'cd.member', items: members } };
        const action = this.api.action('filter', `users/${userId}`, body);

        return await this.api.post(action);
    }
}
